'use client';

import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import type { PermissionKey } from '@/lib/permissions';

export interface AuthUser {
  id: number;
  email: string;
  name: string;
  role_id: number | null;
  role_name: string | null;
  auth_source: string;
  permissions: PermissionKey[];
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (key: PermissionKey) => boolean;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  refresh: async () => {},
  logout: async () => {},
  hasPermission: () => false,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/me');
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    await fetch('/api/auth/logout', { method: 'POST' });
    setUser(null);
    window.location.href = '/login';
  }, []);

  const hasPermission = useCallback((key: PermissionKey) => {
    return !!user?.permissions.includes(key);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, refresh, logout, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
